import { useCallback, useEffect, useRef, useState } from 'react';
import { documentApi, uploadFile, type DocumentMeta } from '../../api';
import DocumentPreviewModal from '../common/DocumentPreviewModal';

type Props = { matterId: number | string };

// Documents tab on the matter detail. Multi-file drag-drop upload that streams
// each file through POST /files (multer → Stratus → files row), then writes a
// `documents` metadata row per file so the existing versioning / OCR / status
// pipeline keeps working unchanged. Drops the legacy two-step presigned-URL
// flow in favour of the MyClinicOS-style server-proxied pattern.

export default function DocumentsPanel({ matterId }: Props) {
  const [docs, setDocs] = useState<DocumentMeta[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [dragOver, setDragOver] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [progress, setProgress] = useState(0);
  const [activeName, setActiveName] = useState<string | null>(null);
  const [previewing, setPreviewing] = useState<DocumentMeta | null>(null);
  const fileInput = useRef<HTMLInputElement | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await documentApi.list({ matter_id: matterId });
      setDocs(r.documents || []);
    } finally { setLoading(false); }
  }, [matterId]);

  useEffect(() => { load(); }, [load]);

  const onUploadFiles = useCallback(async (list: FileList | null) => {
    if (!list || !list.length) return;
    const files = Array.from(list);
    setError(null);
    setUploading(true);
    try {
      for (const f of files) {
        setActiveName(f.name);
        setProgress(0);
        // Server-proxied multipart upload — same flow MyClinicOS uses.
        // Server validates MIME / size, streams into Stratus under
        // tenants/<tenantId>/documents/, and creates a `files` row.
        const r = await uploadFile(f, {
          scope: 'documents',
          parent_table: 'matters',
          parent_row_id: matterId,
          onProgress: setProgress,
        });
        // Mirror the upload as a `documents` metadata row so the existing
        // versioning / OCR / signing flows continue to see it. The `object_id`
        // on documents is the files registry id — download routes resolve it
        // through the same Stratus key.
        await documentApi.create({
          object_id: r.file.id,
          file_name: r.file.name,
          mime_type: r.file.content_type || 'application/octet-stream',
          size_bytes: r.file.byte_size,
          title: f.name.replace(/\.[^.]+$/, ''),
          matter_id: matterId,
          status: 'draft',
          confidentiality: 'internal',
        });
      }
      await load();
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setUploading(false);
      setProgress(0);
      setActiveName(null);
      if (fileInput.current) fileInput.current.value = '';
    }
  }, [matterId, load]);

  const download = (d: DocumentMeta) => {
    // The documents.object_id is the files registry id — go straight to the
    // server-proxied stream. Falls back to the legacy presigned-URL endpoint
    // if needed (older rows where object_id is the raw obj_<ts>_<rand>).
    if (d.object_id && /^\d+$/.test(String(d.object_id))) {
      window.open(`/server/api/files/${d.object_id}/download`, '_blank', 'noopener');
      return;
    }
    documentApi.downloadUrl(d.ROWID)
      .then((r) => window.open(r.download_url, '_blank', 'noopener'))
      .catch((e) => alert((e as Error).message));
  };

  const remove = async (d: DocumentMeta) => {
    if (!confirm(`Delete "${d.title}"?`)) return;
    await documentApi.remove(d.ROWID);
    await load();
  };

  return (
    <div className="stack">
      <div className="row">
        <h3 className="grow" style={{ margin: 0 }}>Documents</h3>
        <button
          className="primary"
          onClick={() => fileInput.current?.click()}
          disabled={uploading}
        >
          {uploading ? 'Uploading…' : '+ Upload files'}
        </button>
      </div>

      <input
        ref={fileInput}
        type="file"
        multiple
        style={{ display: 'none' }}
        onChange={(e) => onUploadFiles(e.target.files)}
      />

      {/* Drag-and-drop dropzone — supports multiple files, shows progress. */}
      <div
        onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
        onDragLeave={() => setDragOver(false)}
        onDrop={(e) => { e.preventDefault(); setDragOver(false); onUploadFiles(e.dataTransfer.files); }}
        onClick={() => !uploading && fileInput.current?.click()}
        style={{
          display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center',
          gap: 6, padding: '18px 14px',
          border: `2px dashed ${dragOver ? 'var(--color-accent)' : 'var(--color-border-strong)'}`,
          borderRadius: 'var(--radius)',
          background: dragOver ? 'rgba(201,163,74,0.06)' : 'var(--input-bg)',
          cursor: uploading ? 'progress' : 'pointer',
          transition: 'border-color 120ms ease, background 120ms ease',
        }}
      >
        {uploading ? (
          <>
            <div style={{ fontSize: 13 }}>
              Uploading {activeName ? <b>{activeName}</b> : '…'}
            </div>
            <div style={{
              width: '60%', height: 6, background: 'var(--color-border)',
              borderRadius: 3, overflow: 'hidden',
            }}>
              <div style={{
                width: `${progress}%`, height: '100%',
                background: 'var(--color-accent)', transition: 'width 120ms ease',
              }} />
            </div>
            <div className="muted" style={{ fontSize: 11 }}>{progress}%</div>
          </>
        ) : (
          <>
            <div style={{ fontSize: 22, color: 'var(--color-text-muted)' }}>⬆︎</div>
            <div style={{ fontSize: 13, color: 'var(--color-text)' }}>
              <span style={{ color: 'var(--color-accent)', fontWeight: 500 }}>Click to upload</span>
              {' '}or drag &amp; drop files
            </div>
            <div className="muted" style={{ fontSize: 11 }}>
              PDF, Word, images, etc. · Up to 50 MB per file · Multi-file supported
            </div>
          </>
        )}
      </div>

      {error && (
        <div className="card" style={{ borderColor: 'var(--color-danger)', color: 'var(--color-danger)' }}>
          {error}
          {error.toLowerCase().includes('bucket') && (
            <div className="muted" style={{ marginTop: 8, fontSize: 12, color: 'var(--color-text-muted)' }}>
              Ask your administrator to create a Stratus bucket and set <code>FILES_BUCKET_NAME</code> in the API function env.
            </div>
          )}
        </div>
      )}

      {previewing && (
        <DocumentPreviewModal
          document={previewing}
          onClose={() => setPreviewing(null)}
        />
      )}

      <div className="card" style={{ padding: 0, overflow: 'hidden' }}>
        {loading ? (
          <div style={{ padding: 18 }} className="muted">Loading…</div>
        ) : docs.length === 0 ? (
          <div style={{ padding: 18 }} className="muted">
            No documents on this matter yet. Upload your first file above.
          </div>
        ) : (
          <table style={{ tableLayout: 'fixed' }}>
            <colgroup>
              <col />                       {/* Title — flexes */}
              <col style={{ width: 70 }} />  {/* Type */}
              <col style={{ width: 90 }} />  {/* Status */}
              <col style={{ width: 130 }} /> {/* Confidentiality */}
              <col style={{ width: 90 }} />  {/* Size */}
              <col style={{ width: 110 }} /> {/* Uploaded */}
              <col style={{ width: 230 }} /> {/* Actions */}
            </colgroup>
            <thead>
              <tr>
                <th>Title</th>
                <th>Type</th>
                <th>Status</th>
                <th>Confidentiality</th>
                <th style={{ textAlign: 'right' }}>Size</th>
                <th>Uploaded</th>
                <th style={{ textAlign: 'right' }}></th>
              </tr>
            </thead>
            <tbody>
              {docs.map((d) => (
                <tr key={String(d.ROWID)}>
                  <td style={{ overflow: 'hidden' }}>
                    <button
                      onClick={() => setPreviewing(d)}
                      className="ghost"
                      style={{
                        padding: 0, border: 'none', background: 'none',
                        textAlign: 'left', cursor: 'pointer', display: 'block',
                        width: '100%', maxWidth: '100%',
                      }}
                      title="Click to preview"
                    >
                      <div style={{
                        fontWeight: 500, color: 'var(--color-accent)',
                        whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis',
                      }}>
                        {d.title}
                      </div>
                      <div className="muted" style={{
                        fontSize: 12, marginTop: 2,
                        whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis',
                      }}>
                        {d.file_name}
                      </div>
                    </button>
                  </td>
                  <td className="muted" style={{ fontSize: 12, textTransform: 'uppercase', letterSpacing: '0.03em' }}>
                    {extOf(d.mime_type, d.file_name)}
                  </td>
                  <td><span className="pill">{d.status.replace('_', ' ')}</span></td>
                  <td>
                    <span className={`pill ${d.confidentiality === 'privileged' || d.confidentiality === 'sealed' ? 'danger' : ''}`}>
                      {d.confidentiality}
                    </span>
                  </td>
                  <td style={{ textAlign: 'right', fontVariantNumeric: 'tabular-nums' }}>
                    {formatSize(d.size_bytes)}
                  </td>
                  <td style={{ whiteSpace: 'nowrap', fontVariantNumeric: 'tabular-nums' }}>
                    {d.CREATEDTIME ? d.CREATEDTIME.slice(0, 10) : '—'}
                  </td>
                  <td style={{ whiteSpace: 'nowrap', textAlign: 'right' }}>
                    <div style={{ display: 'inline-flex', gap: 6, alignItems: 'center' }}>
                      <button
                        onClick={() => setPreviewing(d)}
                        style={{ padding: '4px 10px', fontSize: 12 }}
                      >
                        Preview
                      </button>
                      <button
                        onClick={() => download(d)}
                        style={{ padding: '4px 10px', fontSize: 12 }}
                      >
                        Download
                      </button>
                      <button
                        className="ghost"
                        onClick={() => remove(d)}
                        title="Delete"
                        style={{
                          padding: '4px 8px', fontSize: 14,
                          color: 'var(--color-danger)', lineHeight: 1,
                        }}
                      >
                        ×
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}

// File extension label. Prefer the actual filename extension (".pdf", ".docx")
// — that's what the advocate recognises. Fall back to the mime-type suffix
// (e.g. "vnd.openxmlformats-…" → just "docx-like" → "doc") only when no
// extension is in the filename.
function extOf(mime: string, filename: string): string {
  const m = /\.([a-z0-9]{1,5})$/i.exec(filename || '');
  if (m) return m[1].toLowerCase();
  const tail = (mime || '').split('/').pop() || '';
  if (tail.includes('wordprocessingml')) return 'docx';
  if (tail.includes('spreadsheetml'))    return 'xlsx';
  if (tail.includes('presentationml'))   return 'pptx';
  return tail.slice(0, 4) || '—';
}

function formatSize(bytes: number) {
  if (!bytes) return '—';
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  if (bytes < 1024 * 1024 * 1024) return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  return `${(bytes / (1024 * 1024 * 1024)).toFixed(1)} GB`;
}
